import { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, AlertTriangle, Link2, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { propertyAliasAPI, portfolioPropertiesAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const PLATFORM_LABEL = { uplisting: 'Uplisting', smoobu: 'Smoobu', hostaway: 'Hostaway' };

function propertyKey(platform, propertyId) {
  return `${platform}:${propertyId}`;
}

/**
 * Manual property-identity alias management -- the overlay for duplicate
 * property docs the automatic Hostaway<->Smoobu listing map can't express
 * (e.g. two Uplisting/France docs for the same physical unit). Folded into
 * services.portfolio_scope.build_canonical_properties(), so an alias here
 * takes effect on every portfolio surface at once: Properties tab,
 * Bookings filter, Overview matrices, header counts, missing-costs badge.
 */
export function PropertyAliasPanel() {
  const [loading, setLoading] = useState(false);
  const [aliases, setAliases] = useState([]);
  const [error, setError] = useState(null);

  const [properties, setProperties] = useState([]);
  const [propsLoading, setPropsLoading] = useState(false);

  const [sourceKey, setSourceKey] = useState('');
  const [targetKey, setTargetKey] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [removing, setRemoving] = useState(null);

  const loadAliases = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await propertyAliasAPI.list();
      setAliases(data?.aliases ?? []);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProperties = useCallback(async () => {
    setPropsLoading(true);
    try {
      const { data } = await portfolioPropertiesAPI.getAll();
      const list = (data?.properties ?? []).slice();
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setProperties(list);
    } catch (err) {
      console.error('Failed to load properties:', err);
    } finally {
      setPropsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAliases();
    loadProperties();
  }, [loadAliases, loadProperties]);

  const handleCreate = async () => {
    if (!sourceKey || !targetKey || sourceKey === targetKey) return;
    const [source_platform, ...srcRest] = sourceKey.split(':');
    const [target_platform, ...tgtRest] = targetKey.split(':');
    const source_property_id = srcRest.join(':');
    const target_property_id = tgtRest.join(':');

    setSubmitting(true);
    try {
      await propertyAliasAPI.create({
        source_platform, source_property_id,
        target_platform, target_property_id,
      });
      toast.success('Alias created.');
      setSourceKey('');
      setTargetKey('');
      await loadAliases();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (alias) => {
    setRemoving(alias.id);
    try {
      await propertyAliasAPI.remove(alias.id);
      toast.success(`Removed alias for ${alias.source_property_name || alias.source_property_id}.`);
      await loadAliases();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setRemoving(null);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Property Aliases
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={loadAliases} disabled={loading}>
            {loading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            )}
            Refresh
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Manual overlay for two property docs that are the same physical unit but the
          automatic Hostaway&lt;-&gt;Smoobu listing map can&apos;t express (e.g. duplicate
          Uplisting/France docs). Aliasing a source property to a target folds it into the
          target everywhere: Properties tab, Bookings filter, Overview matrices, and counts.
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        )}

        {/* Add-alias row */}
        <div className="flex flex-wrap items-center gap-2">
          <Select value={sourceKey} onValueChange={setSourceKey} disabled={submitting}>
            <SelectTrigger className="h-9 text-xs flex-1 min-w-[220px]" data-testid="alias-source-select">
              <SelectValue placeholder="Source property (the duplicate)..." />
            </SelectTrigger>
            <SelectContent>
              {propsLoading && (
                <SelectItem value="_loading" disabled>Loading...</SelectItem>
              )}
              {properties.map((p) => (
                <SelectItem key={propertyKey(p.platform, p.property_id)} value={propertyKey(p.platform, p.property_id)}>
                  <span className="truncate">
                    {p.name || p.property_id}
                    <span className="text-muted-foreground ml-1">
                      [{PLATFORM_LABEL[p.platform] || p.platform}]
                    </span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <span className="text-xs text-muted-foreground shrink-0">aliases to</span>

          <Select value={targetKey} onValueChange={setTargetKey} disabled={submitting}>
            <SelectTrigger className="h-9 text-xs flex-1 min-w-[220px]" data-testid="alias-target-select">
              <SelectValue placeholder="Target property (the canonical one)..." />
            </SelectTrigger>
            <SelectContent>
              {propsLoading && (
                <SelectItem value="_loading" disabled>Loading...</SelectItem>
              )}
              {properties.map((p) => (
                <SelectItem key={propertyKey(p.platform, p.property_id)} value={propertyKey(p.platform, p.property_id)}>
                  <span className="truncate">
                    {p.name || p.property_id}
                    <span className="text-muted-foreground ml-1">
                      [{PLATFORM_LABEL[p.platform] || p.platform}]
                    </span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            size="sm"
            className="h-9 text-xs shrink-0"
            disabled={!sourceKey || !targetKey || sourceKey === targetKey || submitting}
            onClick={handleCreate}
            data-testid="alias-add-btn"
          >
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
            Add alias
          </Button>
        </div>

        {/* Existing aliases table */}
        {loading && aliases.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading aliases...
          </div>
        ) : aliases.length === 0 ? (
          <p className="text-sm text-muted-foreground">No property aliases configured.</p>
        ) : (
          <div className="border rounded overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 text-left">
                  <th className="px-3 py-2 font-medium">Source (duplicate)</th>
                  <th className="px-3 py-2 font-medium">Target (canonical)</th>
                  <th className="px-3 py-2 font-medium">Created</th>
                  <th className="px-3 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {aliases.map((a) => (
                  <tr key={a.id} className="border-t">
                    <td className="px-3 py-2">
                      {a.source_property_name || a.source_property_id}
                      <span className="text-muted-foreground ml-1 text-xs">
                        [{PLATFORM_LABEL[a.source_platform] || a.source_platform}]
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {a.target_property_name || a.target_property_id}
                      <span className="text-muted-foreground ml-1 text-xs">
                        [{PLATFORM_LABEL[a.target_platform] || a.target_platform}]
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {a.created_at ? new Date(a.created_at).toLocaleDateString() : ''}
                    </td>
                    <td className="px-3 py-2">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleRemove(a)}
                        disabled={removing === a.id}
                        data-testid={`alias-remove-${a.id}`}
                      >
                        {removing === a.id ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : (
                          <Trash2 className="h-3 w-3 mr-1" />
                        )}
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
